import {
  ComparatorContext,
  ComponentDescriptorOrArray,
  FieldHandler,
  FormInterpreter,
  getRendererFromDescriptor,
  useCurrentForm,
  useFields,
  useTransformers,
  useValidators,
  useWizardService,
} from '@kizi/comparator-core';
import {
  Button,
  ButtonProps,
  Div,
  Heading,
  DivProps,
  P,
  Anchor,
} from '@kizi/core-components';
import React, { useContext, useEffect, useState } from 'react';
import { Form } from './Form';

interface WarningProp {
  title: string;
  text: string;
  link: {
    label: string;
    href: string;
  };
}

export interface StartWidgetProps extends DivProps {
  buttonProps: ButtonProps;
  direction?: 'horizontal' | 'vertical';
  formFields: React.ReactNode;
  fieldNames?: string[];
  title: string;
  isPadded?: boolean;
  warning?: WarningProp;
  registerInAnalytics?: boolean;
  registerInPeregrine?: boolean;
  analyticsName?: string;
  nextStepIndex?: number;
  nextStepName?: string;
  getWidgetDataEvent?: any;
  titleProps?: any;
  footerContent?: any;
  badgeContent?: any;
}

export const StartWidget: React.FC<StartWidgetProps> = ({
  buttonProps,
  direction = 'vertical',
  formFields,
  title,
  children,
  isPadded,
  warning,
  fieldNames = [],
  registerInAnalytics = false,
  registerInPeregrine = false,
  analyticsName,
  nextStepIndex = 1,
  getWidgetDataEvent,
  titleProps,
  footerContent,
  badgeContent,
  ...divProps
}) => {
  const { api, name } = useContext(ComparatorContext);
  const wizard = useWizardService();
  const [form, setForm] = useState<FormInterpreter>();
  const fields = useFields(fieldNames, wizard);

  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    localStorage.removeItem('apollo-cache-persist');
  }, []);

  const onSubmit = (s) => {
    wizard?.send({
      type: 'SET_FIELDS',
      fields: s.context.fields,
    });

    if (registerInAnalytics) {
      window?.dataLayer?.push({
        event: 'start_comparison',
        event_action: 'Click',
        event_label: 'Start met vergelijken',
        event_category: `Funnel - ${analyticsName || name}`,
      });
    }

    goToNextStep();
  };

  const goToNextStep = async () => {
    if (api.sendCustomerData && name === 'os-health-intakecampaign') {
      const firstName = wizard?.state.context.initialSteps[1].fields.find(
        (e) => e?.name === 'firstName',
      )?.value as string;
      const email = wizard?.state.context.initialSteps[2].fields.find(
        (e) => e?.name === 'optionalemail',
      )?.value as string;
      await api.sendCustomerData(firstName, email, 'HEALTH');
      wizard?.send({
        type: 'GOTO',
        index: nextStepIndex,
        meta: { reload: true },
      });
    } else {
      wizard?.send({
        type: 'GOTO',
        index: nextStepIndex,
        meta: { reload: true },
      });
    }
  };

  const validators = useValidators() ?? {};
  const transformers = useTransformers() ?? {};
  const isVertical = direction === 'vertical';
  const flexDirectionContent: 'column' | ['column', 'row'] = isVertical
    ? 'column'
    : ['column', 'row'];
  const widgetVariantHorizontal = isPadded
    ? 'startWidgetHorizontalPadded'
    : 'startWidgetHorizontal';
  const widgetVariant = isVertical ? 'startWidget' : widgetVariantHorizontal;

  const content = children ? (
    children
  ) : (
    <>
      {formFields}
      <Button
        width={['100%', 'fit-content']}
        variant="primary"
        {...buttonProps}
      />
    </>
  );

  const badge = getRendererFromDescriptor(badgeContent);
  const footer = getRendererFromDescriptor(footerContent);

  return (
    <Div
      position="relative"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      variant={widgetVariant}
      {...divProps}
      paddingBottom={footer ? '0px !important' : ''}
    >
      {badge}
      <Heading as="h3" variant="H3" {...titleProps}>
        {title}
      </Heading>
      {warning && (
        <P>
          <b>{warning.title}</b> {warning.text}{' '}
          <Anchor href={warning.link.href}>{warning.link.label}</Anchor>
        </P>
      )}
      <Div
        alignItems="flex-end"
        display="flex"
        flexDirection={flexDirectionContent}
      >
        <Form
          onSubmitted={onSubmit}
          fields={fields.filter((f): f is FieldHandler => !!f)}
          validators={validators}
          transformers={transformers}
          interpreterCallback={(service) => {
            if (!form) setForm(service);
          }}
          keepAlive={false}
        >
          {content}
        </Form>
      </Div>

      {footer}
    </Div>
  );
};

export interface StartWidgetWrapperProps extends StartWidgetProps {
  formFields: ComponentDescriptorOrArray;
}

export const StartWidgetWrapper: React.FC<StartWidgetWrapperProps> = ({
  formFields,
  ...props
}) => {
  const [form] = useCurrentForm();
  if (!form) return null;
  const formFieldsJsx = getRendererFromDescriptor(formFields);
  return <StartWidget {...props} formFields={formFieldsJsx} />;
};
